import React, { useState, useEffect } from 'react';
import { Card, CardHeader, TextField, Box, CardContent, Divider, Grid, Typography, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditCareerPage = () => {
  const { id } = useParams(); // Get the career ID from the URL
  const [editingCareerData, setEditingCareerData] = useState({
    title: '',
    bu_master: '',
    description: '',
    category: '',
    location: '',
    deadline: '',
    is_active: true, // Default value
  });
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCareer = async () => {
      setLoading(true); // Start loading
      try {
        const response = await axios.get(`https://3wzg6m6x-5000.asse.devtunnels.ms/api/career/${id}`);
        console.log('Fetched career data:', response.data); // Log the data to inspect structure
        const careerData = response.data;
        console.log('Category from career data:', careerData.category); // Log the category value
        setEditingCareerData({
          title: careerData.title || '',
          bu_master: careerData.id_bu_master || '',
          description: careerData.description || '',
          category: careerData.type || '',
          location: careerData.location || '',
          deadline: careerData.deadline || '',
          is_active: isActive(careerData.deadline), // Determine if active based on deadline
        });
      } catch (error) {
        console.error('Error fetching career:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    const fetchBuMasters = async () => {
      try {
        const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
        setBuMasters(response.data);
      } catch (error) {
        console.error('Error fetching BU Masters:', error);
      }
    };

    fetchCareer();
    fetchBuMasters();
  }, [id]);

  const isActive = (deadline) => {
    const currentDate = new Date();
    const deadlineDate = new Date(deadline);
    return currentDate < deadlineDate; // Return true if current date is before the deadline
  };

  const handleEditCareer = async () => {
    // Check if all required fields are filled
    if (
      !editingCareerData.title ||
      !editingCareerData.bu_master ||
      !editingCareerData.description ||
      !editingCareerData.category ||
      !editingCareerData.location ||
      !editingCareerData.deadline
    ) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill all the required fields.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('id', id);
    formData.append('title', editingCareerData.title);
    formData.append('id_bu_master', editingCareerData.bu_master);
    formData.append('description', editingCareerData.description);
    formData.append('category', editingCareerData.category);
    formData.append('location', editingCareerData.location);
    formData.append('deadline', editingCareerData.deadline);
    formData.append('is_active', editingCareerData.is_active);

    setLoading(true); // Start loading when the form is submitted
    try {
      const token = localStorage.getItem('token');
      await axios.put('https://3wzg6m6x-5000.asse.devtunnels.ms/api/career/edit', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Career post updated successfully!',
      });

      navigate('/compro/career'); // Redirect after successful edit
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update career post!',
      });
    } finally {
      setLoading(false); // Stop loading after the request is completed
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Edit Career</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        {loading ? ( // Show loading spinner while fetching data
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={editingCareerData.title}
                onChange={(e) => setEditingCareerData({ ...editingCareerData, title: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: 2 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} required>
                <InputLabel>BU Master</InputLabel>
                <Select
                  value={editingCareerData.bu_master}
                  onChange={(e) => setEditingCareerData({ ...editingCareerData, bu_master: e.target.value })}
                  label="BU Master"
                >
                  {buMasters.map((bu) => (
                    <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <ReactQuill
                value={editingCareerData.description}
                onChange={(value) => setEditingCareerData({ ...editingCareerData, description: value })}
                modules={{
                  toolbar: [
                    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['bold', 'italic', 'underline'],
                    ['link'],
                    [{ 'align': [] }],
                    ['image'],
                  ],
                }}
                style={{ height: '150px', width: '100%' }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2, marginTop: 5 }} required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editingCareerData.category}
                  onChange={(e) => setEditingCareerData({ ...editingCareerData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="part_time">Part Time</MenuItem>
                  <MenuItem value="full_time">Full Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                variant="outlined"
                fullWidth
                margin="normal"
                value={editingCareerData.location}
                onChange={(e) => setEditingCareerData({ ...editingCareerData, location: e.target.value })}
                sx={{ borderRadius: 2, marginTop: 5 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Deadline"
                type="date"
                value={editingCareerData.deadline}
                onChange={(e) => setEditingCareerData({ ...editingCareerData, deadline: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: 2 }}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" onClick={handleEditCareer} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default EditCareerPage;
